import React, { useState, useEffect } from 'react';
import {
  Box,
  useTheme,
  Grid,
  Button,
  Divider,
  IconButton,
  CircularProgress,
  Card,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import {
  DeleteOutline as DeleteOutlineIcon,
  Add as AddIcon,
  Restaurant as RestaurantIcon,
  ListAlt as ListIcon,
  AddCircleOutline as AddCircleIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  ChevronLeft as ChevronLeftIcon,
  MedicalInformation as MedicalInformationIcon
} from '@mui/icons-material';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import CustomTypography from 'components/Fileds/CustomTypography';
import CustomAutocomplete from 'components/Fileds/CustomAutocomplete';
import CustomSelect from 'components/Fileds/CustomSelect';
import { useGetRecipeByFoodItemQuery, useGetAllergiesQuery } from './services';

const extractAllergyIds = (data) => {
  if (!data) return [];
  const rawList = Array.isArray(data)
    ? data
    : data.allergies || data.foodItem?.allergies || data.food_item?.allergies || [];
  
  if (!Array.isArray(rawList)) return [];

  return rawList
    .map(a => (typeof a === 'object' && a !== null ? Number(a.id || a.allergy_id) : Number(a)))
    .filter(id => !isNaN(id) && id > 0);
};

const RecipeForm = ({
  onClose,
  initialData,
  onSubmit,
  loading,
  foodItems = [],
  inventoryItems = [],
  recipes = []
}) => {
  const theme = useTheme();
  const isEdit = Boolean(initialData);

  const [foodItemId, setFoodItemId] = useState('');
  const [mainIngredients, setMainIngredients] = useState([]);
  const [addonIngredientsMap, setAddonIngredientsMap] = useState({});
  const [selectedAllergyIds, setSelectedAllergyIds] = useState(() => extractAllergyIds(initialData));
  const [errors, setErrors] = useState({});

  // Fetch full recipe details if editing
  const { data: recipeDetailsResp, isFetching: recipeDetailsLoading } = useGetRecipeByFoodItemQuery(
    initialData?.id,
    { skip: !isEdit || !initialData?.id }
  );

  // Fetch all predefined allergies list
  const { data: allergiesResp, isLoading: allergiesLoading } = useGetAllergiesQuery();
  const allergiesList = allergiesResp?.data?.records || allergiesResp?.records || (Array.isArray(allergiesResp?.data) ? allergiesResp.data : []);

  // Initialize food items select list options
  const foodItemOptions = foodItems
    .filter(item => {
      // If editing, always keep the currently selected food item in the options list
      if (isEdit && String(item.id) === String(initialData?.id)) {
        return true;
      }
      // Otherwise, only show active food items
      if (item.active_status !== undefined && !item.active_status) {
        return false;
      }
      // Hide food items that already have a recipe configured
      const hasRecipe = recipes.some(rec => String(rec.id) === String(item.id));
      return !hasRecipe;
    })
    .map(item => ({
      value: item.id,
      label: item.name
    }));

  const inventoryItemOptions = inventoryItems.map(item => ({
    value: item.id,
    label: `${item.name} (${item.sku_number})`
  }));

  const unitOptions = ["Kg", "gram", "Liter", "Ml", "Piece", "Dozen", "Box"].map(u => ({
    value: u,
    label: u
  }));

  // Handle food item selection
  const handleFoodItemChange = (selectedId) => {
    setFoodItemId(selectedId);
    setErrors(prev => ({ ...prev, foodItemId: null }));
    setMainIngredients([]);

    // Prepopulate empty ingredient arrays for all predefined addons of this food item
    const selectedItem = foodItems.find(item => String(item.id) === String(selectedId));
    const newAddonMap = {};
    if (selectedItem?.groups) {
      selectedItem.groups.forEach(group => {
        if (group.add_ons) {
          group.add_ons.forEach(addOn => {
            newAddonMap[addOn.id] = [];
          });
        }
      });
    }
    setAddonIngredientsMap(newAddonMap);

    const allergyIds = extractAllergyIds(selectedItem);
    setSelectedAllergyIds(allergyIds);
  };

  // Populate form state if editing
  useEffect(() => {
    if (isEdit) {
      if (recipeDetailsResp?.data) {
        const { foodItem, ingredients: mainIngs, addonIngredients: addOnIngs } = recipeDetailsResp.data;
        if (foodItem?.id) setFoodItemId(foodItem.id);

        const extractedIds = extractAllergyIds(recipeDetailsResp.data);
        if (extractedIds.length > 0) {
          setSelectedAllergyIds(extractedIds);
        } else if (initialData) {
          const fallbackIds = extractAllergyIds(initialData);
          if (fallbackIds.length > 0) {
            setSelectedAllergyIds(fallbackIds);
          }
        }

        if (Array.isArray(mainIngs)) {
          setMainIngredients(
            mainIngs.map(ing => ({
              inventory_item_id: ing.inventory_item_id,
              qty: ing.qty,
              unit: ing.unit
            }))
          );
        }

        // Initialize all addons from the food item groups (either from response or local fallback)
        const initialAddonMap = {};
        const targetFoodItem = foodItem?.groups ? foodItem : foodItems.find(item => String(item.id) === String(foodItem?.id || foodItemId));
        if (targetFoodItem?.groups) {
          targetFoodItem.groups.forEach(group => {
            if (group.add_ons) {
              group.add_ons.forEach(addOn => {
                initialAddonMap[addOn.id] = [];
              });
            }
          });
        }

        // Populate addon ingredients if any exist in the database
        if (addOnIngs) {
          addOnIngs.forEach(ing => {
            if (initialAddonMap[ing.addon_id]) {
              initialAddonMap[ing.addon_id].push({
                inventory_item_id: ing.inventory_item_id,
                qty: ing.qty,
                unit: ing.unit
              });
            }
          });
        }
        setAddonIngredientsMap(initialAddonMap);
      } else if (initialData) {
        const initialIds = extractAllergyIds(initialData);
        if (initialIds.length > 0) {
          setSelectedAllergyIds(initialIds);
        }
      }
    }
  }, [recipeDetailsResp, isEdit, initialData, foodItems]);

  // Main Ingredients Action Handlers
  const handleAddMainIngredient = () => {
    setMainIngredients(prev => [...prev, { inventory_item_id: '', qty: '', unit: 'Kg' }]);
  };

  const handleRemoveMainIngredient = (index) => {
    setMainIngredients(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleMainIngredientChange = (index, field, value) => {
    setMainIngredients(prev =>
      prev.map((ing, idx) => {
        if (idx === index) {
          return { ...ing, [field]: value };
        }
        return ing;
      })
    );
  };

  // Addons Action Handlers
  const handleAddAddonIngredient = (addonId) => {
    setAddonIngredientsMap(prev => ({
      ...prev,
      [addonId]: [...(prev[addonId] || []), { inventory_item_id: '', qty: '', unit: 'Kg' }]
    }));
  };

  const handleRemoveAddonIngredient = (addonId, ingredientIndex) => {
    setAddonIngredientsMap(prev => ({
      ...prev,
      [addonId]: (prev[addonId] || []).filter((_, idx) => idx !== ingredientIndex)
    }));
  };

  const handleAddonIngredientChange = (addonId, ingredientIndex, field, value) => {
    setAddonIngredientsMap(prev => {
      const updatedList = (prev[addonId] || []).map((ing, idx) => {
        if (idx === ingredientIndex) {
          return { ...ing, [field]: value };
        }
        return ing;
      });
      return {
        ...prev,
        [addonId]: updatedList
      };
    });
  };

  // Compile list of predefined addons for selected food item
  const selectedFoodItem = foodItems.find(item => String(item.id) === String(foodItemId));
  const availableAddonsList = [];
  if (selectedFoodItem?.groups) {
    selectedFoodItem.groups.forEach(group => {
      if (group.add_ons) {
        group.add_ons.forEach(addOn => {
          if (!availableAddonsList.some(a => a.id === addOn.id)) {
            availableAddonsList.push(addOn);
          }
        });
      }
    });
  }

  // Form validation
  const validate = () => {
    const newErrors = {};
    if (!foodItemId) {
      newErrors.foodItemId = 'Food Item is required';
    }

    // Validate main ingredients
    const mainIngErrors = [];
    mainIngredients.forEach((ing, index) => {
      const err = {};
      if (!ing.inventory_item_id) {
        err.inventory_item_id = 'Required';
      }
      if (!ing.qty || isNaN(Number(ing.qty)) || Number(ing.qty) <= 0) {
        err.qty = 'Invalid Qty';
      }
      if (!ing.unit) {
        err.unit = 'Required';
      }
      if (Object.keys(err).length > 0) {
        mainIngErrors[index] = err;
      }
    });
    if (mainIngErrors.length > 0) {
      newErrors.mainIngredients = mainIngErrors;
    }

    // Validate active addons
    const addonErrors = {};
    Object.keys(addonIngredientsMap).forEach(addonId => {
      const ingList = addonIngredientsMap[addonId] || [];
      const ingErrors = [];

      ingList.forEach((ing, ingIndex) => {
        const err = {};
        if (!ing.inventory_item_id) {
          err.inventory_item_id = 'Required';
        }
        if (!ing.qty || isNaN(Number(ing.qty)) || Number(ing.qty) <= 0) {
          err.qty = 'Invalid Qty';
        }
        if (!ing.unit) {
          err.unit = 'Required';
        }
        if (Object.keys(err).length > 0) {
          ingErrors[ingIndex] = err;
        }
      });

      if (ingErrors.length > 0) {
        addonErrors[addonId] = { ingredients: ingErrors };
      }
    });

    if (Object.keys(addonErrors).length > 0) {
      newErrors.addons = addonErrors;
    }

    const totalIngCount = mainIngredients.length + Object.values(addonIngredientsMap).reduce((sum, list) => sum + list.length, 0);
    if (totalIngCount === 0) {
      newErrors.general = 'Please add at least one ingredient row before saving.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAllergyToggle = (allergyId) => {
    const numId = Number(allergyId);
    setSelectedAllergyIds(prev => {
      if (prev.some(id => Number(id) === numId)) {
        return prev.filter(id => Number(id) !== numId);
      } else {
        return [...prev, numId];
      }
    });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const mainIngredientsPayload = mainIngredients.map(ing => ({
        inventory_item_id: ing.inventory_item_id,
        qty: Number(ing.qty),
        unit: ing.unit
      }));

      const addonsPayload = Object.keys(addonIngredientsMap)
        .filter(addonId => addonIngredientsMap[addonId]?.length > 0)
        .map(addonId => ({
          addon_id: Number(addonId),
          ingredients: addonIngredientsMap[addonId].map(ing => ({
            inventory_item_id: ing.inventory_item_id,
            qty: Number(ing.qty),
            unit: ing.unit
          }))
        }));

      onSubmit({
        food_item_id: foodItemId,
        ingredients: mainIngredientsPayload,
        addons: addonsPayload,
        allergy_ids: selectedAllergyIds
      });
    }
  };

  if (isEdit && recipeDetailsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '1200px', mx: 'auto', mt: 1, px: 2 }}>
      {/* Header Form Wrapper */}
      <form onSubmit={handleFormSubmit}>
        {/* Header Breadcrumbs & Title styled as per Image 2 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={onClose}
            sx={{
              border: '1px solid #E5E7EB',
              borderRadius: '50%',
              bgcolor: '#ffffff',
              color: '#111827',
              width: '40px',
              height: '40px',
              mr: 2,
              '&:hover': {
                bgcolor: '#F3F4F6'
              }
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: '24px' }} />
          </IconButton>
          <CustomTypography
            variant="h4"
            label={isEdit ? "Edit Recipe" : "Add Recipe"}
            sx={{ fontSize: '28px', fontWeight: 700, color: '#111827', mb: 0 }}
          />
        </Box>

        <Divider sx={{ mb: 4, borderColor: '#E5E7EB' }} />

        {/* Form Body Layout */}
        <Grid container spacing={3}>
          {/* Left Column - Core Details */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 3,
                borderRadius: '16px',
                border: '1px solid #E5E7EB',
                boxShadow: 'none',
                height: 'fit-content',
                bgcolor: '#ffffff'
              }}
            >
              {/* Card Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box
                  sx={{
                    bgcolor: '#EFF6FF',
                    p: 1,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px'
                  }}
                >
                  <RestaurantIcon sx={{ color: '#2563EB', fontSize: '20px' }} />
                </Box>
                <CustomTypography
                  label="Core Details"
                  sx={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}
                />
              </Box>

              {/* Select Food Item with CustomAutocomplete */}
              <Box>
                <CustomTypography
                  label="Select Food Item"
                  sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#6B7280',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    mb: 1
                  }}
                />
                <CustomAutocomplete
                  name="food_item_id"
                  options={foodItemOptions}
                  value={
                    foodItemOptions.find(opt => String(opt.value) === String(foodItemId)) ||
                    (isEdit && foodItemId ? { value: foodItemId, label: initialData?.name || '' } : null)
                  }
                  onChange={(_, val) => handleFoodItemChange(val ? val.value : '')}
                  disabled={isEdit}
                  placeholder="Select Food Item"
                  error={Boolean(errors.foodItemId)}
                  helperText={errors.foodItemId}
                  isOptionEqualToValue={(option, value) => option && value && option.value === value.value}
                  getOptionLabel={(option) => option.label || ''}
                />
              </Box>

              {errors.general && (
                <Box sx={{ mt: 2, color: 'error.main', fontSize: '13px', fontWeight: 500 }}>
                  {errors.general}
                </Box>
              )}
            </Card>

            {/* 2. Allergies Card */}
            <Card
              sx={{
                p: 3,
                borderRadius: '16px',
                border: '1px solid #E5E7EB',
                boxShadow: 'none',
                height: 'fit-content',
                bgcolor: '#ffffff',
                mt: 3
              }}
            >
              {/* Card Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    bgcolor: '#FEF3C7',
                    p: 1,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px'
                  }}
                >
                  <MedicalInformationIcon sx={{ color: '#D97706', fontSize: '20px' }} />
                </Box>
                <Box>
                  <CustomTypography
                    label="Allergies"
                    sx={{ fontSize: '16px', fontWeight: 700, color: '#111827', mb: 0 }}
                  />
                  <CustomTypography
                    label="Select all allergens present in this recipe"
                    sx={{ fontSize: '12px', color: '#6B7280' }}
                  />
                </Box>
              </Box>

              <Divider sx={{ mb: 2, borderColor: '#F3F4F6' }} />

              {allergiesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
                </Box>
              ) : allergiesList.length === 0 ? (
                <Box sx={{ py: 2, textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
                  No allergies available
                </Box>
              ) : (
                <FormGroup sx={{ gap: 0.5, maxHeight: '320px', overflowY: 'auto', pr: 0.5 }}>
                  {allergiesList.map(allergy => {
                    const isChecked = selectedAllergyIds.some(id => Number(id) === Number(allergy.id));
                    return (
                      <FormControlLabel
                        key={allergy.id}
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={() => handleAllergyToggle(allergy.id)}
                            size="small"
                            sx={{
                              color: '#D1D5DB',
                              '&.Mui-checked': {
                                color: '#111827'
                              }
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CustomTypography
                              label={allergy.name}
                              sx={{ fontSize: '13px', fontWeight: isChecked ? 600 : 400, color: '#374151' }}
                            />
                            {allergy.scientific_name && (
                              <CustomTypography
                                label={`(${allergy.scientific_name})`}
                                sx={{ fontSize: '11px', color: '#9CA3AF', fontStyle: 'italic' }}
                              />
                            )}
                          </Box>
                        }
                        sx={{
                          mx: 0,
                          px: 1,
                          py: 0.5,
                          borderRadius: '8px',
                          transition: 'background-color 0.15s ease',
                          bgcolor: isChecked ? '#F9FAFB' : 'transparent',
                          '&:hover': {
                            bgcolor: '#F3F4F6'
                          }
                        }}
                      />
                    );
                  })}
                </FormGroup>
              )}
            </Card>
          </Grid>

          {/* Right Column - Config Cards */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              {/* 1. Ingredients & Portions Card - Rendered by default */}
              <Card
                sx={{
                  p: 3,
                  borderRadius: '16px',
                  border: '1px solid #E5E7EB',
                  boxShadow: 'none',
                  bgcolor: '#ffffff'
                }}
              >
                {/* Card Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        bgcolor: '#F3F4F6',
                        p: 1,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '36px',
                        height: '36px'
                      }}
                    >
                      <ListIcon sx={{ color: '#4B5563', fontSize: '20px' }} />
                    </Box>
                    <CustomTypography
                      label="Ingredients"
                      sx={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddMainIngredient}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '8px',
                      borderColor: '#D1D5DB',
                      color: '#111827',
                      fontWeight: 600,
                      px: 2,
                      height: '32px',
                      fontSize: '13px',
                      '&:hover': {
                        borderColor: '#9CA3AF',
                        bgcolor: 'rgba(0, 0, 0, 0.02)'
                      }
                    }}
                  >
                    Add Ingredient
                  </Button>
                </Box>

                {/* Column Header Labels */}
                {mainIngredients.length > 0 && (
                  <Grid container spacing={2} sx={{ mb: 1, px: 0.5 }}>
                    <Grid item xs={5.5}>
                      <CustomTypography
                        label="INVENTORY ITEM"
                        sx={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', letterSpacing: '0.05em' }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <CustomTypography
                        label="QUANTITY"
                        sx={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', letterSpacing: '0.05em' }}
                      />
                    </Grid>
                    <Grid item xs={2.5}>
                      <CustomTypography
                        label="UNIT"
                        sx={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', letterSpacing: '0.05em' }}
                      />
                    </Grid>
                    <Grid item xs={1} />
                  </Grid>
                )}

                {/* Rows */}
                {mainIngredients.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center', color: '#9CA3AF', fontSize: '14px', fontStyle: 'italic' }}>
                    No ingredients added yet. Click " Add Ingredient" to start.
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {mainIngredients.map((ing, index) => {
                      const rowErr = errors.mainIngredients?.[index] || {};
                      return (
                        <Grid container spacing={1.5} alignItems="center" key={index}>
                          {/* Inventory Item with CustomAutocomplete */}
                          <Grid item xs={5.5}>
                            <CustomAutocomplete
                              name={`mainIngredients[${index}].inventory_item_id`}
                              options={inventoryItemOptions}
                              value={inventoryItemOptions.find(opt => String(opt.value) === String(ing.inventory_item_id)) || null}
                              onChange={(_, val) => handleMainIngredientChange(index, 'inventory_item_id', val ? val.value : '')}
                              placeholder="Select Item"
                              isOptionEqualToValue={(option, value) => option && value && option.value === value.value}
                              getOptionLabel={(option) => option.label || ''}
                              error={Boolean(rowErr.inventory_item_id)}
                              helperText={rowErr.inventory_item_id}
                            />
                          </Grid>
                          {/* Quantity */}
                          <Grid item xs={3}>
                            <CustomTextField
                              name={`mainIngredients[${index}].qty`}
                              inputProps={{ inputMode: 'decimal' }}
                              value={ing.qty}
                              onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                                handleMainIngredientChange(index, 'qty', val);
                              }}
                              placeholder="Qty"
                              error={Boolean(rowErr.qty)}
                              helperText={rowErr.qty}
                            />
                          </Grid>
                          {/* Unit dropdown */}
                          <Grid item xs={2.5}>
                            <CustomSelect
                              name={`mainIngredients[${index}].unit`}
                              value={ing.unit}
                              onChange={(e) => handleMainIngredientChange(index, 'unit', e.target.value)}
                              options={unitOptions}
                              placeholder="Unit"
                              error={Boolean(rowErr.unit)}
                              helperText={rowErr.unit}
                            />
                          </Grid>
                          {/* Trash Icon */}
                          <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <IconButton
                              aria-label="delete"
                              onClick={() => handleRemoveMainIngredient(index)}
                              sx={{
                                color: '#EF4444',
                                '&:hover': {
                                  bgcolor: 'rgba(239, 68, 68, 0.04)'
                                }
                              }}
                            >
                              <DeleteOutlineIcon fontSize="medium" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      );
                    })}
                  </Box>
                )}                
              </Card>

              {/* 2. Add-ons Card - Only rendered if food item is selected and has addons */}
              {foodItemId && availableAddonsList.length > 0 && (
                <Card
                  sx={{
                    p: 3,
                    borderRadius: '16px',
                    border: '1px solid #E5E7EB',
                    boxShadow: 'none',
                    bgcolor: '#ffffff'
                  }}
                >
                  {/* Card Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box
                      sx={{
                        bgcolor: '#F3F4F6',
                        p: 1,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '36px',
                        height: '36px'
                      }}
                    >
                      <AddCircleIcon sx={{ color: '#4B5563', fontSize: '20px' }} />
                    </Box>
                    <CustomTypography
                      label="Add-ons"
                      sx={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}
                    />
                  </Box>

                  {/* Addon Blocks */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {availableAddonsList.map((addon) => {
                      const addonIngredients = addonIngredientsMap[addon.id] || [];
                      const addonErr = errors.addons?.[addon.id] || {};

                      return (
                        <Box
                          key={addon.id}
                          sx={{
                            p: 2.5,
                            borderRadius: '12px',
                            border: '1px solid #E5E7EB',
                            bgcolor: '#FCFDFD',
                            position: 'relative'
                          }}
                        >
                          {/* Add-on Name */}
                          <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CustomTypography
                              label="Add-on Name:"
                              sx={{
                                fontSize: '11px',
                                fontWeight: 700,
                                color: '#6B7280',
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                mb: 0
                              }}
                            />
                            <CustomTypography
                              label={addon.name}
                              sx={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#111827',
                                mb:0.5
                              }}
                            />
                          </Box>

                          {/* Add-on Ingredients list */}
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                              <CustomTypography
                                label="ADD-ON INGREDIENTS"
                                sx={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  color: '#6B7280',
                                  letterSpacing: '0.05em'
                                }}
                              />
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddAddonIngredient(addon.id)}
                                sx={{
                                  textTransform: 'none',
                                  borderRadius: '8px',
                                  borderColor: '#D1D5DB',
                                  color: '#111827',
                                  fontWeight: 600,
                                  px: 2,
                                  height: '32px',
                                  fontSize: '13px',
                                  '&:hover': {
                                    borderColor: '#9CA3AF',
                                    bgcolor: 'rgba(0, 0, 0, 0.02)'
                                  }
                                }}
                              >
                                Add Ingredient
                              </Button>
                            </Box>

                            {/* Labels for columns */}
                            {addonIngredients.length > 0 && (
                              <Grid container spacing={2} sx={{ mb: 1, px: 0.5 }}>
                                <Grid item xs={5.5}>
                                  <CustomTypography
                                    label="INVENTORY ITEM"
                                    sx={{ fontSize: '10px', fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.05em' }}
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <CustomTypography
                                    label="QUANTITY"
                                    sx={{ fontSize: '10px', fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.05em' }}
                                  />
                                </Grid>
                                <Grid item xs={2.5}>
                                  <CustomTypography
                                    label="UNIT"
                                    sx={{ fontSize: '10px', fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.05em' }}
                                  />
                                </Grid>
                                <Grid item xs={1} />
                              </Grid>
                            )}

                            {/* Ingredient Rows */}
                            {addonIngredients.length === 0 ? (
                              <Box sx={{ py: 2, fontStyle: 'italic', color: '#9CA3AF', fontSize: '13px', textAlign: 'center' }}>
                                No ingredients added. Click "Add Ingredient" above to configure.
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {addonIngredients.map((ing, ingIdx) => {
                                  const rowErr = addonErr.ingredients?.[ingIdx] || {};
                                  return (
                                    <Grid container spacing={1.5} alignItems="center" key={ingIdx}>
                                      {/* Select Item dropdown with CustomAutocomplete */}
                                      <Grid item xs={5.5}>
                                        <CustomAutocomplete
                                          name={`addonIngredients[${addon.id}][${ingIdx}].inventory_item_id`}
                                          options={inventoryItemOptions}
                                          value={inventoryItemOptions.find(opt => String(opt.value) === String(ing.inventory_item_id)) || null}
                                          onChange={(_, val) => handleAddonIngredientChange(addon.id, ingIdx, 'inventory_item_id', val ? val.value : '')}
                                          placeholder="Select Item"
                                          isOptionEqualToValue={(option, value) => option && value && option.value === value.value}
                                          getOptionLabel={(option) => option.label || ''}
                                          error={Boolean(rowErr.inventory_item_id)}
                                          helperText={rowErr.inventory_item_id}
                                        />
                                      </Grid>
                                      {/* Qty */}
                                      <Grid item xs={3}>
                                        <CustomTextField
                                          name={`addonIngredients[${addon.id}][${ingIdx}].qty`}
                                          inputProps={{ inputMode: 'decimal' }}
                                          value={ing.qty}
                                          onChange={(e) => {
                                            const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                                            handleAddonIngredientChange(addon.id, ingIdx, 'qty', val);
                                          }}
                                          placeholder="Qty"
                                          error={Boolean(rowErr.qty)}
                                          helperText={rowErr.qty}
                                        />
                                      </Grid>
                                      {/* Unit */}
                                      <Grid item xs={2.5}>
                                        <CustomSelect
                                          name={`addonIngredients[${addon.id}][${ingIdx}].unit`}
                                          value={ing.unit}
                                          onChange={(e) => handleAddonIngredientChange(addon.id, ingIdx, 'unit', e.target.value)}
                                          options={unitOptions}
                                          placeholder="Unit"
                                          error={Boolean(rowErr.unit)}
                                          helperText={rowErr.unit}
                                        />
                                      </Grid>
                                      {/* Trash button */}
                                      <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <IconButton
                                          aria-label="delete addon ingredient"
                                          onClick={() => handleRemoveAddonIngredient(addon.id, ingIdx)}
                                          sx={{
                                            color: '#EF4444',
                                            '&:hover': {
                                              bgcolor: 'rgba(239, 68, 68, 0.04)'
                                            }
                                          }}
                                        >
                                          <DeleteOutlineIcon fontSize="medium" />
                                        </IconButton>
                                      </Grid>
                                    </Grid>
                                  );
                                })}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Card>
              )}

            </Box>

            {/* Action Buttons at the bottom right */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, pb: 4 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  borderColor: '#D1D5DB',
                  color: '#111827',
                  fontWeight: 600,
                  px: 3,
                  height: '40px',
                  fontSize: '14px',
                  '&:hover': {
                    borderColor: '#9CA3AF',
                    bgcolor: 'rgba(0, 0, 0, 0.02)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  bgcolor: '#000000',
                  color: '#ffffff',
                  fontWeight: 600,
                  px: 3,
                  height: '40px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    bgcolor: '#1f2937'
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#D1D5DB',
                    color: '#9CA3AF'
                  }
                }}
              >
                {loading ? (
                  <CircularProgress size="1rem" sx={{ color: 'white' }} />
                ) : (
                  <>
                    <SaveIcon fontSize="small" />
                    {isEdit ? 'Update Recipe' : 'Save Recipe'}
                  </>
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default RecipeForm;
